/**
 * @file: AiUpgradeCard.tsx
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ArrowRight, Sparkles } from 'lucide-react-native';
import { memo, useMemo } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';

/** The edition a store upgrades from. */
export type AiUpgradeFrom = 'workshop' | 'ai';

/** What the full edition adds, told to each edition short of it. */
const kCopy: Record<AiUpgradeFrom, { title: string; body: string }> = {
  ai: {
    title: '升级知车全功能版',
    body: '让门店运营更高效：增加营收、降低运营成本，并更深入地了解门店经营状况。',
  },
  workshop: {
    title: '升级知车全功能版，解锁全部 AI 能力',
    body: '私域营销、销售对练、经营分析、朋友圈海报一次开通：让 AI 帮门店拉新留客、提升成交，把经营数据变成决策。',
  },
};

/** The invitation to the full edition, at the end of AI home. */
export const AiUpgradeCard = memo(
  ({
    from,
    busy,
    onPress,
  }: {
    from: AiUpgradeFrom;
    busy: boolean;
    onPress: () => void;
  }) => {
    const copy = kCopy[from];
    const { rpx } = useResponsiveContext();
    const styles = useStyles();
    return (
      <View style={styles.card}>
        <View style={styles.bubble} />
        <View style={styles.icon}>
          <Sparkles size={18} color={aiColors.primaryForeground} />
        </View>
        <Typography.Text
          bold
          size={rpx(16)}
          color={aiColors.primaryForeground}
          style={{ marginTop: rpx(12) }}
        >
          {copy.title}
        </Typography.Text>
        <Typography.Text
          size={rpx(14)}
          lineHeight={rpx(21)}
          color="rgba(255,255,255,0.8)"
          style={{ marginTop: rpx(6) }}
        >
          {copy.body}
        </Typography.Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="立即咨询"
          disabled={busy}
          onPress={onPress}
          style={({ pressed }) => [styles.button, pressed && { opacity: 0.85 }]}
        >
          {busy ? (
            <ActivityIndicator color={aiColors.primary} />
          ) : (
            <>
              <Typography.Text medium size={rpx(14)} color={aiColors.primary}>
                立即咨询
              </Typography.Text>
              <ArrowRight size={16} color={aiColors.primary} />
            </>
          )}
        </Pressable>
      </View>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        card: {
          marginTop: rpx(22),
          backgroundColor: aiColors.primary,
          borderRadius: rpx(14),
          padding: rpx(20),
          overflow: 'hidden',
        },
        bubble: {
          position: 'absolute',
          right: -rpx(48),
          top: -rpx(48),
          width: rpx(144),
          height: rpx(144),
          borderRadius: rpx(72),
          backgroundColor: 'rgba(255,255,255,0.1)',
        },
        icon: {
          width: rpx(36),
          height: rpx(36),
          borderRadius: rpx(10),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255,255,255,0.15)',
        },
        button: {
          marginTop: rpx(16),
          height: rpx(44),
          borderRadius: rpx(12),
          backgroundColor: '#fff',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: rpx(8),
        },
      }),
    [rpx],
  );
}
